import React, { createContext, useState, useEffect, useContext } from 'react';
import userAuthService from '../services/userAuthService';

// Create the user auth context
export const UserAuthContext = createContext();

/**
 * User Authentication Context Provider
 * Manages regular user authentication state and provides auth-related functions
 */
export const UserAuthContextProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Initialize user auth state on mount
  useEffect(() => {
    const initAuth = async () => {
      try {
        // Check if user is already authenticated
        if (userAuthService.isAuthenticated()) {
          // Get user from localStorage first for immediate UI update
          const storedUser = userAuthService.getUser();
          if (storedUser) {
            setUser(storedUser);
            setIsAuthenticated(true);
          }

          // Then fetch fresh user data from API
          try {
            const currentUser = await userAuthService.getCurrentUser();
            setUser(currentUser);
            setIsAuthenticated(true);
          } catch (err) {
            // If API call fails, user might be logged out or token expired
            console.warn('Failed to get current user, logging out:', err);
            userAuthService.logout();
            setUser(null);
            setIsAuthenticated(false);
          }
        }
      } catch (err) {
        console.error('User auth initialization error:', err);
        setError(err.message || 'Authentication error');
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  /**
   * Login regular user
   * @param {string} email - User email
   * @param {string} password - User password
   */
  const login = async (email, password) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await userAuthService.login(email, password);
      
      // Handle different possible response formats
      let userData = null;
      
      // Format 1: response.data.data.user (nested)
      if (response?.data?.data?.user) {
        userData = response.data.data.user;
      } 
      // Format 2: response.data.user (direct)
      else if (response?.data?.user) {
        userData = response.data.user;
      }
      // Format 3: response.user (direct from service)
      else if (response?.user) {
        userData = response.user;
      }
      
      if (userData) {
        setUser(userData);
        setIsAuthenticated(true);
        return userData;
      } else {
        console.error('Unexpected login response format:', response);
        throw new Error('Invalid response format from server');
      }
    } catch (err) {
      const errorMessage = err.message || 'Login failed. Please try again.';
      console.error('Login error:', { error: err, message: errorMessage });
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Logout current user
   */
  const logout = async () => {
    setLoading(true);
    
    try {
      await userAuthService.logout();
    } catch (err) {
      console.error('User logout error:', err);
    } finally {
      setUser(null);
      setIsAuthenticated(false);
      setLoading(false);
    }
  };

  /**
   * Update current user's password
   */
  const updatePassword = async (currentPassword, newPassword) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await userAuthService.updatePassword(currentPassword, newPassword);
      return result;
    } catch (err) {
      setError(err.response?.data?.message || 'Password update failed');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Context value
  const value = {
    user,
    loading,
    error,
    isAuthenticated,
    isAdmin: false, // Always false for regular user auth context
    login,
    logout,
    updatePassword,
    clearError: () => setError(null)
  };

  return <UserAuthContext.Provider value={value}>{children}</UserAuthContext.Provider>;
};

// Custom hook for using user auth context
export const useUserAuth = () => {
  const context = useContext(UserAuthContext);
  if (!context) {
    throw new Error('useUserAuth must be used within a UserAuthContextProvider');
  }
  return context;
};
